import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ChatBubbleLeftRightIcon,
    UserGroupIcon,
    ChatBubbleBottomCenterTextIcon,
    ClockIcon,
    CalendarIcon,
    ArrowTrendingUpIcon,
    MagnifyingGlassIcon,
    DocumentArrowDownIcon,
    XMarkIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    UserIcon,
    CalendarDaysIcon,
    ChartBarIcon,
    ArrowPathIcon,
    PaperAirplaneIcon,
    ChatBubbleOvalLeftIcon,
    SparklesIcon,
    TrashIcon,
    ArchiveBoxIcon,
    ClipboardDocumentIcon,
    CheckIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow, format } from 'date-fns';
import toast from 'react-hot-toast';
import Plot from 'react-plotly.js';
import { adminAPI } from '../../services/api';

const ChatAnalytics = () => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [timeframe, setTimeframe] = useState('7days');
    const [selectedUser, setSelectedUser] = useState(null);
    const [userChats, setUserChats] = useState([]);
    const [userChatsLoading, setUserChatsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedSections, setExpandedSections] = useState({
        overview: true,
        topUsers: true,
        trends: true,
        recentMessages: true,
        commonQuestions: true
    });
    const [showUserModal, setShowUserModal] = useState(false);
    const [copiedMessageId, setCopiedMessageId] = useState(null);
    const [expandedMessages, setExpandedMessages] = useState({});

    const timeframeOptions = [
        { value: '24hours', label: 'Last 24 Hours', icon: ClockIcon },
        { value: '7days', label: 'Last 7 Days', icon: CalendarIcon },
        { value: '30days', label: 'Last 30 Days', icon: CalendarDaysIcon },
        { value: 'all', label: 'All Time', icon: ChartBarIcon }
    ];

    useEffect(() => {
        loadAnalytics();
    }, [timeframe]);

    const loadAnalytics = async () => {
        setLoading(true);
        try {
            const response = await adminAPI.getChatAnalytics(timeframe);
            setAnalytics(response.data);
        } catch (error) {
            toast.error('Failed to load chat analytics');
        } finally {
            setLoading(false);
        }
    };

    const loadUserChats = async (userId) => {
        setUserChatsLoading(true);
        try {
            const response = await adminAPI.getUserChatHistory(userId, 100);
            setUserChats(response.data.messages || []);
            setSelectedUser(analytics?.userMessageCounts?.find(u => u.id === userId));
            setShowUserModal(true);
        } catch (error) {
            toast.error('Failed to load user chats');
        } finally {
            setUserChatsLoading(false);
        }
    };

    const exportData = async () => {
        try {
            const response = await adminAPI.exportChatData();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `chat-data-${Date.now()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Data exported successfully');
        } catch (error) {
            toast.error('Failed to export data');
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const copyToClipboard = async (text, messageId) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedMessageId(messageId);
            toast.success('Copied to clipboard!');
            setTimeout(() => setCopiedMessageId(null), 2000);
        } catch (err) {
            toast.error('Failed to copy');
        }
    };

    const toggleMessageExpand = (messageId) => {
        setExpandedMessages(prev => ({
            ...prev,
            [messageId]: !prev[messageId]
        }));
    };

    // Simple CSS Bar Chart Component
    const SimpleBarChart = ({ data }) => {
        if (!data || data.length === 0) return null;
        
        const maxValue = Math.max(...data.map(d => d.message_count));
        
        return (
            <div className="space-y-2">
                {data.slice(0, 10).map((item, index) => (
                    <div key={index} className="flex items-center">
                        <div className="w-24 text-sm text-gray-600">
                            {format(new Date(item.date), 'MMM dd')}
                        </div>
                        <div className="flex-1 ml-4">
                            <div className="relative h-8 flex items-center">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${(item.message_count / maxValue) * 100}%` }}
                                    transition={{ duration: 0.5, delay: index * 0.05 }}
                                    className="absolute left-0 h-6 bg-gradient-to-r from-primary-500 to-primary-400 rounded-lg"
                                />
                                <span className="absolute left-2 text-xs font-medium text-white mix-blend-difference">
                                    {item.message_count} messages
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Enhanced Chat Message Component
    const ChatMessage = ({ chat, index }) => {
        const isExpanded = expandedMessages[chat.id];
        const shouldTruncate = chat.message?.length > 200 || chat.response?.length > 300;
        const displayMessage = isExpanded ? chat.message : chat.message?.substring(0, 200);
        const displayResponse = isExpanded ? chat.response : chat.response?.substring(0, 300);

        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group relative"
            >
                {/* Timeline connector */}
                {index > 0 && (
                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200 -translate-y-6" />
                )}
                
                <div className="relative flex gap-4 mb-6">
                    {/* Avatar with icon */}
                    <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-lg">
                            <ChatBubbleOvalLeftIcon className="h-6 w-6 text-white" />
                        </div>
                    </div>

                    {/* Message content */}
                    <div className="flex-1 space-y-3">
                        {/* Timestamp */}
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                            <CalendarIcon className="h-3 w-3" />
                            {format(new Date(chat.created_at), 'PPP')}
                            <ClockIcon className="h-3 w-3 ml-2" />
                            {format(new Date(chat.created_at), 'p')}
                        </div>

                        {/* User message */}
                        <div className="bg-blue-50 rounded-2xl rounded-tl-md p-4 border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <PaperAirplaneIcon className="h-4 w-4 text-blue-600 rotate-45" />
                                    <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">User Question</span>
                                </div>
                                <button
                                    onClick={() => copyToClipboard(chat.message, `q-${chat.id}`)}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-blue-100 rounded-lg"
                                >
                                    {copiedMessageId === `q-${chat.id}` ? (
                                        <CheckIcon className="h-4 w-4 text-green-600" />
                                    ) : (
                                        <ClipboardDocumentIcon className="h-4 w-4 text-blue-600" />
                                    )}
                                </button>
                            </div>
                            <p className="text-gray-800 leading-relaxed">
                                {displayMessage}
                                {shouldTruncate && !isExpanded && '...'}
                            </p>
                            {shouldTruncate && (
                                <button
                                    onClick={() => toggleMessageExpand(chat.id)}
                                    className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                                >
                                    {isExpanded ? 'Show less' : 'Read more'}
                                </button>
                            )}
                        </div>

                        {/* AI Response */}
                        {chat.response && (
                            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl rounded-tr-md p-4 border border-green-100 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <SparklesIcon className="h-4 w-4 text-green-600" />
                                        <span className="text-xs font-semibold text-green-600 uppercase tracking-wide">AI Response</span>
                                    </div>
                                    <button
                                        onClick={() => copyToClipboard(chat.response, `a-${chat.id}`)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-green-100 rounded-lg"
                                    >
                                        {copiedMessageId === `a-${chat.id}` ? (
                                            <CheckIcon className="h-4 w-4 text-green-600" />
                                        ) : (
                                            <ClipboardDocumentIcon className="h-4 w-4 text-green-600" />
                                        )}
                                    </button>
                                </div>
                                <p className="text-gray-800 leading-relaxed">
                                    {displayResponse}
                                    {(chat.response?.length > 300 && !isExpanded) && '...'}
                                </p>
                                {chat.response?.length > 300 && (
                                    <button
                                        onClick={() => toggleMessageExpand(chat.id)}
                                        className="mt-2 text-xs text-green-600 hover:text-green-700 font-medium"
                                    >
                                        {isExpanded ? 'Show less' : 'Read more'}
                                    </button>
                                )}

                                {/* Inline SHAP plot — shown when the message carried one */}
                                {chat.shap_plot && chat.shap_plot.data && (
                                    <div className="mt-3 bg-white rounded-xl border border-gray-200 p-3">
                                        <Plot
                                            data={chat.shap_plot.data}
                                            layout={{
                                                ...chat.shap_plot.layout,
                                                autosize: true,
                                                margin: { l: 150, r: 30, t: 40, b: 40 },
                                            }}
                                            config={{ responsive: true, displaylogo: false }}
                                            style={{ width: '100%', height: '360px' }}
                                            useResizeHandler={true}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Metadata tags */}
                        {chat.metadata && Object.keys(chat.metadata).length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(chat.metadata).map(([key, value]) => (
                                    <span key={key} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                        {key}: {value}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
        );
    };

    // User Stats Card Component
    const UserStatsCard = ({ user }) => (
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                    <span className="text-primary-600 font-bold text-lg">
                        {user.username?.[0]?.toUpperCase()}
                    </span>
                </div>
                <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{user.username}</h3>
                    <p className="text-xs text-gray-500">{user.email}</p>
                </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 bg-blue-50 rounded-lg">
                    <p className="text-xl font-bold text-blue-600">{user.today_count || 0}</p>
                    <p className="text-xs text-gray-500">Today</p>
                </div>
                <div className="text-center p-2 bg-green-50 rounded-lg">
                    <p className="text-xl font-bold text-green-600">{user.week_count || 0}</p>
                    <p className="text-xs text-gray-500">Week</p>
                </div>
                <div className="text-center p-2 bg-purple-50 rounded-lg">
                    <p className="text-xl font-bold text-purple-600">{user.month_count || 0}</p>
                    <p className="text-xs text-gray-500">Month</p>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
                    <p className="text-gray-600">Loading analytics...</p>
                </div>
            </div>
        );
    }

    const filteredUsers = analytics?.userMessageCounts?.filter(user =>
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
                >
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Chat Analytics</h1>
                        <p className="text-gray-600">Track user conversations and engagement</p>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                        {/* Timeframe Selector */}
                        <div className="flex bg-white rounded-lg shadow-sm border border-gray-200 p-1">
                            {timeframeOptions.map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => setTimeframe(option.value)}
                                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        timeframe === option.value
                                            ? 'bg-primary-600 text-white'
                                            : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    <option.icon className="h-4 w-4 mr-2" />
                                    {option.label}
                                </button>
                            ))}
                        </div>

                        {/* Export Button */}
                        <button
                            onClick={exportData}
                            className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            <DocumentArrowDownIcon className="h-5 w-5 mr-2 text-gray-600" />
                            Export CSV
                        </button>

                        {/* Refresh Button */}
                        <button
                            onClick={loadAnalytics}
                            className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            title="Refresh"
                        >
                            <ArrowPathIcon className="h-5 w-5 text-gray-600" />
                        </button>
                    </div>
                </motion.div>

                {/* Overview Stats */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
                >
                    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between">
                            <div className="p-3 bg-blue-50 rounded-xl">
                                <UserGroupIcon className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="text-right">
                                <p className="text-3xl font-bold text-gray-900">{analytics?.totalStats?.active_users || 0}</p>
                                <p className="text-sm text-gray-500">Active Users</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between">
                            <div className="p-3 bg-green-50 rounded-xl">
                                <ChatBubbleLeftRightIcon className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="text-right">
                                <p className="text-3xl font-bold text-gray-900">{analytics?.totalStats?.total_messages || 0}</p>
                                <p className="text-sm text-gray-500">Total Messages</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between">
                            <div className="p-3 bg-purple-50 rounded-xl">
                                <ChatBubbleBottomCenterTextIcon className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="text-right">
                                <p className="text-3xl font-bold text-gray-900">
                                    {Math.round(analytics?.totalStats?.avg_message_length || 0)}
                                </p>
                                <p className="text-sm text-gray-500">Avg. Message Length</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between">
                            <div className="p-3 bg-yellow-50 rounded-xl">
                                <ClockIcon className="h-6 w-6 text-yellow-600" />
                            </div>
                            <div className="text-right">
                                <p className="text-sm font-bold text-gray-900">
                                    {analytics?.totalStats?.last_activity 
                                        ? formatDistanceToNow(new Date(analytics.totalStats.last_activity), { addSuffix: true })
                                        : 'Never'}
                                </p>
                                <p className="text-sm text-gray-500">Last Activity</p>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Message Trend Chart */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100"
                >
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                            <ArrowTrendingUpIcon className="h-5 w-5 mr-2 text-primary-600" />
                            Message Trends (Last 30 Days)
                        </h2>
                    </div>
                    <SimpleBarChart data={analytics?.messageTrend || []} />
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                    {/* Top Users by Messages */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="lg:col-span-2 bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden"
                    >
                        <div 
                            className="p-6 border-b border-gray-100 flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => toggleSection('topUsers')}
                        >
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <UserGroupIcon className="h-5 w-5 mr-2 text-primary-600" />
                                Top Users by Messages
                            </h2>
                            {expandedSections.topUsers ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                        </div>

                        <AnimatePresence>
                            {expandedSections.topUsers && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="overflow-hidden"
                                >
                                    <div className="p-4 border-b border-gray-100">
                                        <div className="relative">
                                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Search users..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            />
                                        </div>
                                    </div>

                                    <div className="overflow-y-auto max-h-96 divide-y divide-gray-100">
                                        {filteredUsers?.map((user, index) => (
                                            <motion.div
                                                key={user.id}
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: index * 0.02 }}
                                                className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                                                onClick={() => loadUserChats(user.id)}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                                                            <span className="text-primary-600 font-bold">
                                                                {user.username?.[0]?.toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-900">{user.username}</p>
                                                            <p className="text-sm text-gray-500">{user.email}</p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-2xl font-bold text-primary-600">{user.message_count}</p>
                                                        <p className="text-xs text-gray-400">messages</p>
                                                    </div>
                                                </div>
                                                
                                                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                                                    <div className="bg-blue-50 p-2 rounded-lg">
                                                        <p className="font-semibold text-blue-600">{user.today_count || 0}</p>
                                                        <p className="text-gray-500">Today</p>
                                                    </div>
                                                    <div className="bg-green-50 p-2 rounded-lg">
                                                        <p className="font-semibold text-green-600">{user.week_count || 0}</p>
                                                        <p className="text-gray-500">This Week</p>
                                                    </div>
                                                    <div className="bg-purple-50 p-2 rounded-lg">
                                                        <p className="font-semibold text-purple-600">{user.month_count || 0}</p>
                                                        <p className="text-gray-500">This Month</p>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>

                    {/* Common Questions */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden"
                    >
                        <div 
                            className="p-6 border-b border-gray-100 flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => toggleSection('commonQuestions')}
                        >
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <ChatBubbleBottomCenterTextIcon className="h-5 w-5 mr-2 text-primary-600" />
                                Common Questions
                            </h2>
                            {expandedSections.commonQuestions ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                        </div>

                        <AnimatePresence>
                            {expandedSections.commonQuestions && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="overflow-y-auto max-h-96 divide-y divide-gray-100"
                                >
                                    {analytics?.commonQuestions?.map((question, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.02 }}
                                            className="p-4 hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <p className="text-sm text-gray-800 flex-1 leading-relaxed">
                                                    {question.message?.length > 100 
                                                        ? question.message.substring(0, 100) + '...' 
                                                        : question.message}
                                                </p>
                                                <span className="bg-primary-100 text-primary-600 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                                                    {question.ask_count} {question.ask_count === 1 ? 'time' : 'times'}
                                                </span>
                                            </div>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                </div>

                {/* Recent Messages */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden"
                >
                    <div 
                        className="p-6 border-b border-gray-100 flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => toggleSection('recentMessages')}
                    >
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                            <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-primary-600" />
                            Recent Conversations
                        </h2>
                        {expandedSections.recentMessages ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                    </div>

                    <AnimatePresence>
                        {expandedSections.recentMessages && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="divide-y divide-gray-100">
                                    {analytics?.recentMessages?.map((message, index) => (
                                        <motion.div
                                            key={message.id}
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.02 }}
                                            className="p-4 hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center space-x-3">
                                                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                                                        <span className="text-primary-600 font-bold text-sm">
                                                            {message.username?.[0]?.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900">{message.username}</p>
                                                        <p className="text-xs text-gray-400">
                                                            {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="ml-11 space-y-3">
                                                <div className="bg-blue-50 p-3 rounded-xl">
                                                    <p className="text-xs text-blue-600 mb-1 font-semibold">User:</p>
                                                    <p className="text-sm text-gray-800 leading-relaxed">{message.message}</p>
                                                </div>
                                                
                                                {message.response && (
                                                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 rounded-xl">
                                                        <p className="text-xs text-green-600 mb-1 font-semibold">AI Response:</p>
                                                        <p className="text-sm text-gray-800 leading-relaxed">
                                                            {message.response.length > 150 
                                                                ? message.response.substring(0, 150) + '...' 
                                                                : message.response}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>

                {/* Enhanced User Chat History Modal */}
                <Transition appear show={showUserModal} as={Fragment}>
                    <Dialog as="div" className="relative z-50" onClose={() => setShowUserModal(false)}>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
                        </Transition.Child>

                        <div className="fixed inset-0 overflow-y-auto">
                            <div className="flex min-h-full items-center justify-center p-4">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-out duration-300"
                                    enterFrom="opacity-0 scale-95"
                                    enterTo="opacity-100 scale-100"
                                    leave="ease-in duration-200"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                                        {/* Modal Header with Gradient */}
                                        <div className="relative bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5">
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                                                        <UserIcon className="h-7 w-7 text-white" />
                                                    </div>
                                                    <div>
                                                        <Dialog.Title className="text-xl font-bold text-white">
                                                            Chat History
                                                        </Dialog.Title>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <p className="text-white/90 font-medium">{selectedUser?.username}</p>
                                                            <span className="text-white/60 text-sm">•</span>
                                                            <p className="text-white/70 text-sm">{selectedUser?.email}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => setShowUserModal(false)}
                                                    className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                                                >
                                                    <XMarkIcon className="h-6 w-6" />
                                                </button>
                                            </div>

                                            {/* User Stats Summary */}
                                            {selectedUser && (
                                                <div className="mt-4 flex gap-3">
                                                    <div className="bg-white/10 backdrop-blur rounded-lg px-3 py-1.5">
                                                        <p className="text-white/70 text-xs">Total Messages</p>
                                                        <p className="text-white font-bold text-lg">{selectedUser.message_count}</p>
                                                    </div>
                                                    <div className="bg-white/10 backdrop-blur rounded-lg px-3 py-1.5">
                                                        <p className="text-white/70 text-xs">This Month</p>
                                                        <p className="text-white font-bold text-lg">{selectedUser.month_count}</p>
                                                    </div>
                                                    <div className="bg-white/10 backdrop-blur rounded-lg px-3 py-1.5">
                                                        <p className="text-white/70 text-xs">This Week</p>
                                                        <p className="text-white font-bold text-lg">{selectedUser.week_count}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Modal Content */}
                                        <div className="p-6">
                                            {userChatsLoading ? (
                                                <div className="py-20 text-center">
                                                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                                                    <p className="mt-4 text-gray-500">Loading conversations...</p>
                                                </div>
                                            ) : userChats.length === 0 ? (
                                                <div className="py-20 text-center">
                                                    <ArchiveBoxIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                                                    <p className="text-gray-500">No chat history found for this user</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                                                    {userChats.map((chat, index) => (
                                                        <ChatMessage key={chat.id || index} chat={chat} index={index} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Modal Footer */}
                                        <div className="border-t border-gray-100 px-6 py-4 bg-gray-50 flex justify-between items-center">
                                            <p className="text-sm text-gray-500">
                                                Total {userChats.length} messages
                                            </p>
                                            <button
                                                onClick={() => setShowUserModal(false)}
                                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium"
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </Dialog>
                </Transition>
            </div>

            {/* Custom Scrollbar Styles */}
            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #a8a8a8;
                }
            `}</style>
        </div>
    );
};

export default ChatAnalytics;